import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, mergeDeep, parsePreviewLink } from '@/utils/utils';
import {
  AdditionalFields,
  Appearance,
  BookingType,
  Calendar,
  Conference,
  Configuration,
  EmailTemplate,
  EventReminder,
  GroupConfiguration,
  GroupEvent,
  GroupEventAPIData,
  ImportGroupEventDetails,
  NotificationType,
  RecurrenceDeleteOption,
  RecurrenceUpdateOption,
  ThemeConfig,
} from '@nylas/core';
import { Component, Element, Event, EventEmitter, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import TabContentsGroup, { TabGroup } from './tab-contents-group';
import { User } from '@/common/nylas-api-request';
import { CONFIGURATION_EVENT_TYPE, editorGroupTabComponentNames } from '@/common/constants';
import { DataResponseReturnType } from '@/common/types';
import * as Sentry from '@sentry/browser';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { CreateGroupEventFormState } from '../../..';
import { SchedulerGroupEventDetail } from '@nylas/core';
import { ErrorCategory, Notification } from '@/types/index';
import { version } from '@/version';

/**
 * The nylas-editor-tabs-group component provides the user interface for managing editor tabs within the scheduler editor.
 * It is primarily used to control the edit mode of the scheduler editor, and it is rendered automatically by the
 * parent component, nylas-scheduler-editor, during editing. There is no need to interact with this component directly
 * or set any props manually, as its behavior is fully managed by the parent component.
 *
 * @part nap__title - [nylas-additional-participants] The title of the component.
 * @part nap__subtitle - [nylas-additional-participants] The subtitle of the component.
 * @part nap__content - [nylas-additional-participants] The content of the component.
 * @part nap__input - [nylas-additional-participants] The input of the component.
 * @part nap__remove-participant - [nylas-additional-participants] The remove participant button of the component.
 * @part nap__add-participant - [nylas-additional-participants] The add participant button of the component.
 * @part nap__header - [nylas-availability-picker] The header of the availability picker
 * @part nap__select-timezone - [nylas-availability-picker] The timezone selection container
 * @part nap__select-timezone-button -[nylas-availability-picker]  The timezone selection button
 * @part nap__select-timezone-dropdown-content -[nylas-availability-picker] The timezone selection dropdown content
 * @part nap__availability - [nylas-availability-picker] The availability container
 * @part nap__day - [nylas-availability-picker] The day container
 * @part nap__time-ranges - [nylas-availability-picker] The time ranges container
 * @part nap__time-range - [nylas-availability-picker] The time range container
 * @part nap__add-time-range - [nylas-availability-picker] The add time range button
 * @part nap__time-picker-container -[nylas-availability-picker]  The time picker container
 * @part nap__time-picker-input - [nylas-availability-picker]  The time picker input
 * @part nap__time-picker-times - [nylas-availability-picker] The time picker times
 * @part nbcp - [nylas-booking-calendar-picker] The booking calendar picker container
 * @part nbcp__header - [nylas-booking-calendar-picker] The header of the booking calendar picker
 * @part nbcp__input-label - [nylas-booking-calendar-picker] The input label of the booking calendar picker
 * @part nbcp__dropdown - [nylas-booking-calendar-picker] The dropdown container
 * @part nbcp__dropdown-button - [nylas-booking-calendar-picker] The dropdown button
 * @part nbcp__dropdown-content - [nylas-booking-calendar-picker] The dropdown content
 * @part nbt - [nylas-buffer-time] The buffer time container
 * @part nbt__header - [nylas-buffer-time] The header of the buffer time
 * @part nbt__body - [nylas-buffer-time] The body of the buffer time
 * @part nbt__dropdown-before - [nylas-buffer-time] The before buffer dropdown container
 * @part nbt__dropdown-button-before - [nylas-buffer-time] The before buffer dropdown button
 * @part nbt__dropdown-content-before - [nylas-buffer-time] The before buffer dropdown content
 * @part nbt__dropdown-after - [nylas-buffer-time] The after buffer dropdown container
 * @part nbt__dropdown-button-after - [nylas-buffer-time] The after buffer dropdown button
 * @part nbt__dropdown-content-after - [nylas-buffer-time] The after buffer dropdown content
 * @part nbt__preview - [nylas-buffer-time] The buffer time preview
 * @part ncp - [nylas-calendar-picker] The calendar picker container
 * @part ncp__header - [nylas-calendar-picker] The header of the calendar picker
 * @part ncp__dropdown - [nylas-calendar-picker] The dropdown container
 * @part ncp__dropdown-button - [nylas-calendar-picker] The dropdown button
 * @part ncp__dropdown-content - [nylas-calendar-picker] The dropdown content
 * @part ncbf - [nylas-custom-booking-flow] The custom booking flow container
 * @part ncbf__header - [nylas-custom-booking-flow] The header of the custom booking flow
 * @part ncbf__dropdown - [nylas-custom-booking-flow] The dropdown container
 * @part ncbf__dropdown-button - [nylas-custom-booking-flow] The dropdown button
 * @part ncbf__dropdown-content - [nylas-custom-booking-flow] The dropdown content
 * @part nedesc - [nylas-event-description] The event description container
 * @part nedesc__textarea - [nylas-event-description] The event description textarea
 * @part ned - [nylas-event-duration] The event duration container
 * @part ned__dropdown - [nylas-event-duration] The dropdown container for the duration increment
 * @part ned__dropdown-button - [nylas-event-duration] The dropdown button for the duration increment
 * @part ned__dropdown-content - [nylas-event-duration] The dropdown content for the duration increment
 * @part ned__input_dropdown - [nylas-event-duration] The input dropdown container for the duration minutes
 * @part ned__input_dropdown-input - [nylas-event-duration] The input for the duration minutes
 * @part ned__input_dropdown-content - [nylas-event-duration] The dropdown content for the input duration minutes
 * @part net - [nylas-event-title] The event title container
 * @part net__title - [nylas-event-title] The event title input
 * @part net__dropdown-content - [nylas-event-title] The token options container
 * @part nlfb - [nylas-limit-future-bookings] The limit future bookings container
 * @part nlfb__number-dropdown - [nylas-limit-future-bookings] The number dropdown container
 * @part nlfb__number-dropdown-button - [nylas-limit-future-bookings] The number dropdown button
 * @part nlfb__number-dropdown-content - [nylas-limit-future-bookings] The number dropdown content
 * @part nlfb__period-dropdown - [nylas-limit-future-bookings] The period dropdown container
 * @part nlfb__period-dropdown-button - [nylas-limit-future-bookings] The period dropdown button
 * @part nlfb__period-dropdown-content - [nylas-limit-future-bookings] The period dropdown content
 * @part nel - [nylas-event-location] The event location container
 * @part nel__location - [nylas-event-location] The event location input
 * @part nel__dropdown - [nylas-event-location] The dropdown container
 * @part nel__dropdown-button - [nylas-event-location] The dropdown button
 * @part nel__dropdown-content - [nylas-event-location] The dropdown content
 * @part nmbn - [nylas-min-booking-notice] The minimum booking notice container
 * @part nmbn__number-dropdown - [nylas-min-booking-notice] The number dropdown container
 * @part nmbn__number-dropdown-button - [nylas-min-booking-notice] The number dropdown button
 * @part nmbn__number-dropdown-content -[nylas-min-booking-notice] The number dropdown content
 * @part nmbn__period-dropdown - [nylas-min-booking-notice] The period dropdown container
 * @part nmbn__period-dropdown-button - [nylas-min-booking-notice] The period dropdown button
 * @part nmbn__period-dropdown-content - [nylas-min-booking-notice] The period dropdown content
 * @part nmcn - [nylas-min-cancellation-notice] The minimum cancellation notice container
 * @part nmcn__number-dropdown - [nylas-min-cancellation-notice] The number dropdown container
 * @part nmcn__number-dropdown-button - [nylas-min-cancellation-notice] The number dropdown button
 * @part nmcn__number-dropdown-content - [nylas-min-cancellation-notice] The number dropdown content
 * @part nmcn__period-dropdown - [nylas-min-cancellation-notice] The period dropdown container
 * @part nmcn__period-dropdown-button - [nylas-min-cancellation-notice] The period dropdown button
 * @part nmcn__period-dropdown-content - [nylas-min-cancellation-notice] The period dropdown content
 * @part ncpolicy - [nylas-cancellation-policy] The cancellation policy container
 * @part ncpolicy__textarea - [nylas-cancellation-policy] The cancellation policy textarea
 * @part nti - [nylas-timeslot-interval] The timeslot interval container
 * @part nti__header - [nylas-timeslot-interval] The header of the timeslot interval picker
 * @part nti__input-label - [nylas-timeslot-interval] The input label of the timeslot interval picker
 * @part nti__dropdown - [nylas-timeslot-interval] The dropdown container
 * @part nti__dropdown-button - [nylas-timeslot-interval] The dropdown button
 * @part nti__dropdown-content - [nylas-timeslot-interval] The dropdown content
 * @part npca - [nylas-participants-custom-availability] The participants custom availability container
 * @part npca__header - [nylas-participants-custom-availability] The header of the participants custom availability
 * @part npca__content - [nylas-participants-custom-availability] The content of the participants custom availability
 * @part npca__participant-container - [nylas-participants-custom-availability] The participant container
 * @part npca__participant-title - [nylas-participants-custom-availability] The participant title
 * @part npca__participant-toggle--container - [nylas-participants-custom-availability] The participant toggle container
 * @part npca__toggle-label - [nylas-participants-custom-availability] The toggle label
 * @part npca__toggle-input - [nylas-participants-custom-availability] The toggle input
 * @part npca__toggle-slider - [nylas-participants-custom-availability] The toggle slider
 * @part ncbs - [nylas-customize-booking-settings] The booking calendar picker container
 * @part ncbs__header - [nylas-customize-booking-settings] The header of the booking calendar picker
 * @part ncbs__settings - [nylas-customize-booking-settings] The settings container
 * @part ncbs__settings-div - [nylas-customize-booking-settings] The div inside the settings container that contains the checkbox and tooltip for each setting
 * @part ncbs__additional_guests - [nylas-customize-booking-settings] The additional guests setting
 * @part ncbs__cancellation_options - [nylas-customize-booking-settings] The cancellation options setting
 * @part ncbs__rescheduling_options - [nylas-customize-booking-settings] The rescheduling options setting
 * @part nsm - [nylas-scheduling-method] The booking calendar picker container
 * @part nsm__header - [nylas-scheduling-method] The header of the booking calendar picker
 * @part nsm__input-label - [nylas-scheduling-method] The input label of the booking calendar picker
 * @part nsm__dropdown - [nylas-scheduling-method] The dropdown container
 * @part nsm__dropdown-button - [nylas-scheduling-method] The dropdown button
 * @part nsm__dropdown-content - [nylas-scheduling-method] The dropdown content
 * @part npn - The nylas-page-name container
 * @part npn__body - The body of the event communication section
 * @part npn__header - The header of the event communication section
 * @part npn__drawer-toggle--container - The card's drawer toggle container
 * @part npn__input-textfield - The page name textfield
 * @part editor__form-contents - The form contents container
 * @part editor__tab - The tabs container
 * @part editor__tab-content - The tab content container
 * @part editor__footer - The footer container
 * @part editor__footer-preview - The preview button container
 * @part editor__footer-buttons - The buttons container
 */
@Component({
  tag: 'nylas-editor-tabs-group',
  styleUrl: 'nylas-editor-tabs-group.scss',
  scoped: true,
})
export class NylasEditorTabsGroup {
  /**
   * The form reference to access the form data.
   */
  private formRef!: HTMLFormElement;
  /**
   * The host element.
   */
  @Element() host!: HTMLNylasEditorTabsGroupElement;
  // Props
  /**
   * @standalone
   * The list of calendars to use in the editor when configuring availability.
   */
  @Prop() calendars?: Calendar[];
  /**
   * @standalone
   * The selected configuration to use in the editor when editing an existing configuration or creating a new one.
   */
  @Prop() selectedConfiguration?: GroupConfiguration;

  /**
   * @standalone
   * The list of configurations
   */
  @Prop() configurations?: (Configuration | GroupConfiguration)[];

  /**
   * @standalone
   * The current logged in user.
   */
  @Prop() currentUser?: User;
  /**
   * @standalone
   * The scheduler preview link to use when the user clicks on the preview button.
   * You can use a placeholder `{config.id}` to replace the configuration id anywhere in the link.
   */
  @Prop({ attribute: 'scheduler-preview-link' }) schedulerPreviewLink: string = '';
  /**
   * @standalone
   * The mode of the editor.
   * - `app`: The editor is used as a standalone app.
   * - `composable`: The editor is used as a composable component.
   */
  @Prop() mode?: 'app' | 'composable' = 'app';
  /**
   * @standalone
   * This prop is passed down by the parent component.
   * It is an optional prop is used to hide tabs in the editor. Available tabs are:
   * eventInfo, availability, participants, bookingOptions, bookingForm
   */
  @Prop() hideEditorTabs?: TabGroup[] = [];
  /**
   * @standalone
   * This prop is passed down by the parent component to enable or disable user feedback.
   */
  @Prop() enableUserFeedback?: boolean = true;

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The selected event type.
   */
  @Prop() selectedEventType?: CONFIGURATION_EVENT_TYPE;

  /**
   * The selected group event.
   */
  @Prop() selectedGroupEvent?: GroupEventAPIData;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  // State
  /**
   * The action to perform in the editor.
   * - `create`: Create a new configuration.
   * - `edit`: Edit an existing configuration.
   */
  @State() action: 'create' | 'edit' = 'create';
  /**
   * The state for showing the feedback modal.
   */
  @State() showFeedbackModal: boolean = false;
  /**
   * The active tab in the editor.
   */
  @State() activeTab: TabGroup = TabGroup.CalendarView;
  /**
   * The loading state of the editor.
   */
  @State() isLoading: boolean = false;
  /**
   * The component loaded state of the editor.
   */
  @State() componentLoaded: boolean = false;
  /**
   * The unsaved changes state of the editor.
   */
  @State() hasUnsavedChanges: boolean = false;
  /**
   * The changes saved state of the editor.
   */
  @State() changesSaved: boolean = false;
  /**
   * The form state of the editor.
   */
  @State() formState: Partial<{
    title: string;
    description: string;
    participants: {
      name: string;
      email: string;
      is_organizer?: boolean;
    }[];
    additionalFields?: Record<string, AdditionalFields>;
    bookingCalendar?: string;
    location?: string;
    conferencing?: Conference;
    bookingType?: string;
    cancellationPolicy?: string;
    minCancellationNotice?: number;
    minBookingNotice?: number;
    hideCancellationOptions?: boolean;
    hideReschedulingOptions?: boolean;
    reminders?: EventReminder[];
    emailTemplate?: EmailTemplate;
    redirectUrl?: string;
    slug: string;
    appearance: Appearance;
    name: string;
    eventCapacity: number;
    eventTimeslot: {
      when: {
        start_time: number;
        end_time: number;
        start_timezone: string;
        end_timezone: string;
      };
      recurrence: string[];
    };
    disableEmails?: boolean;
    organizerLocale?: string;
  }> = {};
  /**
   * The error state of the editor.
   */
  @State() error?: {
    message: string;
    type: NotificationType | undefined;
    category: ErrorCategory | undefined;
    title: string;
  } = {
    message: '',
    type: NotificationType.Error,
    category: ErrorCategory.Component,
    title: '',
  };

  // Events
  /**
   * Event emitted when the group scheduler configuration form is submitted and ready to be processed (before the API request is made). This fires for both create and edit actions after client-side validation passes.
   */
  @Event() schedulerConfigChanged!: EventEmitter<SchedulerGroupEventDetail & { host: HTMLNylasEditorTabsGroupElement }>;
  /**
   * Event emitted when a group scheduler configuration is successfully created via the API. This fires only for create actions after the API request completes successfully.
   */
  @Event() schedulerConfigCreated!: EventEmitter<SchedulerGroupEventDetail & { host: HTMLNylasEditorTabsGroupElement }>;
  /**
   * Event emitted when the user clicks the cancel button to exit the group scheduler editor.
   */
  @Event() cancelButtonClick!: EventEmitter<void>;
  /**
   * Event emitted at the very beginning of form submission, before any validation occurs. This event is cancelable and can prevent the submission process if preventDefault() is called.
   */
  @Event({ bubbles: true, cancelable: true }) formSubmitted!: EventEmitter<void>;
  /**
   * Event emitted when the user clicks the preview button. This fires before the preview link is opened and can be used to override the default preview behavior.
   */
  @Event() previewButtonClicked!: EventEmitter<HTMLNylasEditorTabsGroupElement>;
  /**
   * Event emitted whenever any form field value changes within the group scheduler editor. This provides real-time updates as users modify form inputs.
   */
  @Event() schedulerEditorFormUpdated!: EventEmitter<{ value: string; name: string }>;

  /**
   * Event emitted when the user requests to create a new group event in the calendar view. This fires when the create button is clicked and validation passes.
   */
  @Event() groupEventCreateTriggered!: EventEmitter<{ groupEvent: Partial<GroupEvent>; configId: string; host: HTMLNylasEditorTabsGroupElement }>;

  /**
   * Event emitted when the user requests to save changes to an existing group event. This fires after validation passes and includes the recurrence update option chosen by the user.
   */
  @Event() groupEventUpdateTriggered!: EventEmitter<{
    groupEvent: GroupEvent;
    chosenOption: RecurrenceUpdateOption;
    configId: string;
    host: HTMLNylasEditorTabsGroupElement;
  }>;

  /**
   * Event emitted when the user requests to delete a group event. This fires when the delete button is clicked and includes the recurrence delete option chosen by the user.
   */
  @Event() groupEventDeleteTriggered!: EventEmitter<{
    groupEvent: GroupEvent;
    chosenOption: RecurrenceDeleteOption;
    configId: string;
    host: HTMLNylasEditorTabsGroupElement;
  }>;

  /**
   * Event emitted when the user requests to import group events from an external source. This fires when the import action is triggered in the calendar view.
   */
  @Event() groupEventImportTriggered!: EventEmitter<{
    groupEvent: ImportGroupEventDetails;
    configId: string;
    host: HTMLNylasEditorTabsGroupElement;
  }>;

  /**
   * Event emitted when validation errors prevent saving changes to a group event. This provides error details for handling failed save attempts.
   */
  @Event() saveGroupEventChangesError!: EventEmitter<{
    error: string;
    configId: string;
    host: HTMLNylasEditorTabsGroupElement;
  }>;

  /**
   * Event emitted when the component needs to display an error notification to the user. This includes validation errors, API errors, and other component-level errors.
   */
  @Event({ cancelable: false }) readonly nylasSchedulerError!: EventEmitter<{ notification: Notification; host: HTMLElement }>;

  /**
   * Event emitted when the user dismisses an error notification, allowing parent components to handle cleanup or additional actions.
   */
  @Event({ cancelable: false }) dismissNotification!: EventEmitter<{ id: string; host: HTMLNylasEditorTabsGroupElement }>;

  // Watchers
  /**
   * Watches for changes in the selected configuration and updates the form state.
   */
  @Watch('selectedConfiguration')
  configChangedHandler(newConfig: GroupConfiguration) {
    debug('[nylas-editor-tabs-group]', 'configChangedHandler', newConfig, this.selectedConfiguration);
    this.formState = {
      ...this.getFormStateFromConfig(newConfig),
    };
    if (newConfig?.id) {
      this.action = 'edit';
    } else {
      this.action = 'create';
    }
  }

  /**
   * Watches for changes in the selected group event and updates the form state.
   */
  @Watch('selectedGroupEvent')
  selectedGroupEventChangedHandler(newConfig: GroupEventAPIData) {
    debug('[nylas-editor-tabs-group]', 'selectedGroupEventChangedHandler', newConfig, this.selectedConfiguration);
    this.formState = {
      ...this.formState,
      title: this.selectedGroupEvent?.event?.title,
      description: this.selectedGroupEvent?.event?.description,
      location: this.selectedGroupEvent?.event?.location,
      conferencing: this.selectedGroupEvent?.event?.conferencing,
      eventCapacity: this.selectedGroupEvent?.group_event_info?.capacity,
      participants: this.selectedGroupEvent?.group_event_info?.participants?.map(participant => ({
        name: participant.name,
        email: participant.email,
        is_organizer: !!participant.is_organizer,
      })),
      eventTimeslot: this.selectedGroupEvent?.event?.when
        ? {
            when: {
              start_time: this.selectedGroupEvent?.event?.when?.start_time as number,
              end_time: this.selectedGroupEvent?.event?.when?.end_time as number,
              start_timezone: this.selectedGroupEvent?.event?.when?.start_timezone as string,
              end_timezone: this.selectedGroupEvent?.event?.when?.end_timezone as string,
            },
            recurrence: this.selectedGroupEvent?.group_event_info?.rrule ? this.selectedGroupEvent?.group_event_info?.rrule : [],
          }
        : undefined,
    };
  }

  /**
   * Watches for changes in the current user and updates the form state.
   */
  @Watch('currentUser')
  userChangedHandler(newUser: User) {
    // Actively watch for changes in the current user to update the form state.
    debug('[nylas-editor-tabs-group]', 'userChangedHandler', newUser);
    if (newUser) {
      this.formState = {
        ...this.getFormStateFromConfig(this.selectedConfiguration),
      };
    }
  }

  /**
   * Watches for changes in the selected language and updates the form state.
   */
  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    if (this.error && this.error.message) {
      this.error = {
        ...this.error,
        message: i18next.t('fixFormErrors'),
      };
      this.showErrorNotification(this.error);
    }
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  // Methods
  /**
   * Event handler for the scheduler config created event.
   */
  @Method()
  async schedulerConfigCreatedEventHandler(data: SchedulerGroupEventDetail & { host: HTMLNylasEditorTabsGroupElement }) {
    const event = this.schedulerConfigCreated.emit(data);
    return event;
  }

  /**
   * Sets the error state of the editor.
   */
  @Method()
  async setError(error: { message: string; type: NotificationType; category: ErrorCategory; title: string }) {
    this.error = {
      message: error.message,
      type: error.type,
      category: error.category,
      title: error.title || i18next.t('genericErrorTitle'),
    };
    if (this.error?.message) {
      this.showErrorNotification(this.error);
    }
  }

  /**
   * Resets the error state of the editor.
   */
  @Method()
  async resetError() {
    if (this.error?.message) {
      this.dismissNotification.emit({ id: this.error?.message.split(' ').join('-'), host: this.host });
    }
    this.error = {
      message: '',
      type: NotificationType.Error,
      category: ErrorCategory.Component,
      title: '',
    };
  }

  // Lifecycle methods
  connectedCallback() {
    debug('[nylas-editor-tabs-group]', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('[nylas-editor-tabs-group]', 'componentWillLoad');
    this.formState = this.getFormStateFromConfig(this.selectedConfiguration);
  }

  componentDidLoad() {
    debug('[nylas-editor-tabs-group]', 'componentDidLoad');
    if (this.enableUserFeedback && !Sentry.isInitialized()) {
      Sentry.init({
        dsn: 'https://9d5731f1c77ca84c9ed3fb9b3ccf7ee1@o74852.ingest.us.sentry.io/4507889638178816',
        release: version,
        integrations: integrations => [
          ...integrations.filter(_integration => false), // Removes default integrations (including Breadcrumbs)
          Sentry.feedbackIntegration({
            colorScheme: 'system',
            autoInject: false,
          }),
        ],
        // Disable all other event capture
        autoSessionTracking: false, // Disable session tracking
        beforeSend: () => null, // Prevents error events from being sent
      });
    }
    if (this.selectedConfiguration?.id) {
      this.configChangedHandler(this.selectedConfiguration);
    }
    const firstVisibleTab = (this.host.querySelector('button.tab:not(.hide)') as HTMLButtonElement)?.name as TabGroup;
    if (firstVisibleTab && this.hideEditorTabs?.includes(this.activeTab)) {
      this.activeTab = firstVisibleTab;
    }
    this.updateFormState();
    this.componentLoaded = true;
  }

  disconnectedCallback() {
    debug('[nylas-editor-tabs-group]', 'disconnectedCallback');
  }

  // Event listeners
  /**
   * Event listener for the value changed event.
   */
  @Listen('valueChanged')
  handleValueChanged(event: CustomEvent) {
    debug('[nylas-editor-tabs-group]', 'handleValueChanged', event);
    const { name, value } = event.detail;
    debug('name', name, 'value', value);
    this.setFormState(value, name);
    this.formState = { ...this.formState };
    if (editorGroupTabComponentNames.includes(name)) {
      this.hasUnsavedChanges = true;
    }
    this.host.resetError();
    this.schedulerEditorFormUpdated.emit({
      value: event.detail.value,
      name: event.detail.name,
    });
  }

  /**
   * Event listener for the feedback modal closed event.
   */
  @Listen('feedbackModalClosed')
  feedbackModalClosedHandler() {
    this.showFeedbackModal = false;
  }

  /**
   * Event listener for the feedback submitted event.
   */
  @Listen('feedbackSubmitted')
  feedbackSubmittedHandler(event: CustomEvent<{ feedback: string }>) {
    const eventId = Sentry.captureMessage('Scheduler Editor User Feedback');
    const feedback = {
      eventId: eventId,
      message: event.detail.feedback,
    };

    Sentry.captureFeedback(feedback, {
      includeReplay: true,
      captureContext: {
        tags: {
          'nylas-web-element': 'nylas-scheduler-editor',
          'nylas-web-element-version': version,
        },
        extra: {
          configId: this.selectedConfiguration?.id,
          slug: this.selectedConfiguration?.slug,
        },
      },
    });
    this.showFeedbackModal = false;
  }

  /**
   * Event listener for the import group event event.
   */
  @Listen('importGroupEvent')
  importGroupEventHandler(event: CustomEvent<ImportGroupEventDetails>) {
    this.groupEventImportTriggered.emit({ groupEvent: event.detail, configId: this.selectedConfiguration?.id ?? '', host: this.host });
  }

  /**
   * Event listener for the save group event changes event.
   */
  @Listen('saveGroupEventChanges')
  saveGroupEventChangesHandler(event: CustomEvent<{ data: GroupEventAPIData; action: RecurrenceUpdateOption }>) {
    debug('saveGroupEventChangesHandler', event.detail, this.selectedGroupEvent);
    const valid = this.formRef.checkValidity();
    const groupEvent = this.getGroupEventDetailsFromFormState();
    this.resetError();
    if (!groupEvent.title) {
      this.setError({
        message: i18next.t('nylasEditorTabsGroup.eventTitleRequired'),
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.formErrorTitle'),
      });
    }
    if (!groupEvent.when?.start_time || !groupEvent.when?.end_time) {
      this.setError({
        message: i18next.t('nylasEditorTabsGroup.eventTimeRequired'),
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.formErrorTitle'),
      });
    }
    if (!groupEvent.participants?.length) {
      this.setError({
        message: i18next.t('nylasEditorTabsGroup.participantsRequired'),
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.formErrorTitle'),
      });
    }
    if (groupEvent.participants?.length && groupEvent.participants?.some(p => !p.email)) {
      this.setError({
        message: i18next.t('nylasEditorTabsGroup.participantEmailRequired'),
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.formErrorTitle'),
      });
    }
    if (this.error?.message !== '' || !valid) {
      this.saveGroupEventChangesError.emit({
        error: this.error?.message ?? i18next.t('nylasEditorTabsGroup.errorOccured'),
        configId: this.selectedConfiguration?.id ?? '',
        host: this.host,
      });
      return;
    }

    this.groupEventUpdateTriggered.emit({
      groupEvent: groupEvent,
      chosenOption: event.detail.action,
      configId: this.selectedConfiguration?.id ?? '',
      host: this.host,
    });
  }

  /**
   * Event listener for the delete group event event.
   */
  @Listen('deleteGroupEvent')
  deleteGroupEventHandler(event: CustomEvent<{ data: GroupEventAPIData; action: RecurrenceDeleteOption }>) {
    debug('deleteGroupEventHandler', event.detail);
    const groupEvent = this.getGroupEventDetailsFromFormState();
    this.groupEventDeleteTriggered.emit({
      groupEvent: groupEvent,
      chosenOption: event.detail.action,
      configId: this.selectedConfiguration?.id ?? '',
      host: this.host,
    });
  }

  /**
   * Event listener for the create group event event.
   */
  @Listen('createGroupEvent')
  createGroupEventHandler(event: CustomEvent<CreateGroupEventFormState>) {
    const formState = event.detail;
    const organizer = {
      name: this.currentUser?.name ?? this.currentUser?.email ?? '',
      email: this.currentUser?.email ?? '',
    };
    const organizerDefaultCal = this.calendars?.find(c => c.is_primary) ?? this.calendars?.[0];
    const conferencing = this.formState?.conferencing || formState.conferencing;
    const groupEvent = {
      title: formState.title,
      description: formState.description,
      location: !conferencing ? formState.location : undefined,
      participants: formState.participants,
      organizer: organizer,
      conferencing: !formState.location ? conferencing : undefined,
      when: formState.eventTimeslot.when,
      recurrence: formState.eventTimeslot.recurrence,
      calendar_id: organizerDefaultCal?.id || 'primary',
      capacity: formState.eventCapacity,
    };
    this.groupEventCreateTriggered.emit({ groupEvent, configId: this.selectedConfiguration?.id ?? '', host: this.host });
  }

  showErrorNotification(error: { message: string; type: NotificationType | undefined; category: ErrorCategory | undefined; title: string }) {
    this.nylasSchedulerError.emit({
      notification: {
        title: error.title,
        category: error.category || ErrorCategory.Component,
        type: error.type || NotificationType.Error,
        description: error.message,
        id: error.message.split(' ').join('-'),
        ttl: 'none',
      },
      host: this.host,
    });
  }

  /**
   * Gets the group event details from the form state.
   */
  getGroupEventDetailsFromFormState() {
    const organizerDefaultCal = this.calendars?.find(c => c.is_primary) ?? this.calendars?.[0];
    const organizer = {
      name: this.currentUser?.name ?? this.currentUser?.email ?? '',
      email: this.currentUser?.email ?? '',
    };
    const participantOrganizer = [{ ...organizer, is_organizer: true }];
    const { title, description, bookingCalendar, location, conferencing, eventCapacity, eventTimeslot, participants } = this.formState;

    return {
      title: title,
      description: description,
      location: !conferencing ? location : undefined,
      conferencing: !location ? conferencing : undefined,
      when: {
        start_time: eventTimeslot?.when?.start_time ?? 0,
        end_time: eventTimeslot?.when?.end_time ?? 0,
        start_timezone: eventTimeslot?.when?.start_timezone ?? '',
        end_timezone: eventTimeslot?.when?.end_timezone ?? '',
      },
      recurrence: eventTimeslot?.recurrence ?? [],
      calendar_id: bookingCalendar || organizerDefaultCal?.id || 'primary',
      participants: participants?.length ? participants : participantOrganizer,
      organizer: organizer,
      capacity: eventCapacity || 10,
    } as any;
  }

  /**
   * Gets the form state from the selected configuration.
   */
  getFormStateFromConfig(config?: GroupConfiguration) {
    if (config?.appearance) {
      debug('Appearance settings have been returned from the given configuration; please remember to grab them from the configSettingsLoaded event and apply them as desired.');
    }
    return {
      additionalFields: config?.scheduler?.additional_fields ?? {},
      bookingType: config?.group_booking?.booking_type ?? 'booking',
      cancellationPolicy: config?.scheduler?.cancellation_policy ?? '',
      minCancellationNotice: config?.scheduler?.min_cancellation_notice ?? 0,
      minBookingNotice: config?.scheduler?.min_booking_notice ?? 60,
      hideCancellationOptions: config?.scheduler?.hide_cancellation_options ?? false,
      hideReschedulingOptions: config?.scheduler?.hide_rescheduling_options ?? false,
      reminders: config?.group_booking?.reminders,
      emailTemplate: config?.scheduler?.email_template,
      redirectUrl: config?.scheduler?.confirmation_redirect_url,
      slug: config?.slug,
      appearance: config?.appearance,
      name: config?.name,
      bookingCalendar: config?.group_booking?.calendar_id,
      disableEmails: config?.group_booking?.disable_emails,
      organizerLocale: config?.scheduler?.email_template?.organizer_locale,
    };
  }

  /**
   * Sets the form state from the form data.
   */
  setFormState(value, key) {
    switch (key) {
      case 'title':
        this.formState.title = value.toString();
        break;
      case 'description':
        this.formState.description = value.toString();
        break;
      case 'location':
        this.formState.location = value.toString();
        break;
      case 'conference':
        this.formState.conferencing = JSON.parse(value.toString());
        break;
      case 'confirmation-type':
        this.formState.bookingType = value.toString();
        break;
      case 'min-cancellation-notice':
        this.formState.minCancellationNotice = parseInt(value.toString());
        break;
      case 'min-booking-notice':
        this.formState.minBookingNotice = parseInt(value.toString());
        break;
      case 'cancellation-policy':
        this.formState.cancellationPolicy = value.toString();
        break;
      case 'customize-booking-settings': {
        const settings = JSON.parse(value.toString());
        this.formState.hideCancellationOptions = settings.hideCancellationOptions;
        this.formState.hideReschedulingOptions = settings.hideReschedulingOptions;
        break;
      }
      case 'booking-calendar':
        this.formState.bookingCalendar = value.toString();
        break;
      case 'event-capacity':
        this.formState.eventCapacity = parseInt(String(value));
        break;
      case 'additional-fields':
        this.formState.additionalFields = JSON.parse(value.toString());
        break;
      case 'confirmation-email-template':
        this.formState.emailTemplate = JSON.parse(value.toString());
        break;
      case 'reminder-overrides':
        this.formState.reminders = JSON.parse(value.toString());
        break;
      case 'confirmation-redirect':
        this.formState.redirectUrl = value.toString();
        break;
      case 'custom-event-slug':
        debug('custom-event-slug', value);
        this.formState.slug = JSON.parse(value.toString());
        break;
      case 'page-styling':
        this.formState.appearance = JSON.parse(value.toString());
        break;
      case 'page-name':
        this.formState.name = JSON.parse(value.toString());
        break;
      case 'participants': {
        const participants = JSON.parse(value.toString());
        this.formState.participants = participants.map(participant => ({
          name: participant.name,
          email: participant.email,
          is_organizer: participant.is_organizer,
          ...(participant.grant_id ? { grant_id: participant.grant_id } : {}),
        }));
        break;
      }
      case 'event-timeslot':
        this.formState.eventTimeslot = JSON.parse(value.toString());
        break;
      case 'disable-emails': {
        const { disableEmails, organizerLocale } = JSON.parse(value.toString());
        this.formState.disableEmails = disableEmails;
        this.formState.organizerLocale = organizerLocale;
        break;
      }
    }
    this.schedulerEditorFormUpdated.emit({
      value,
      name: key,
    });
  }

  /**
   * Updates the form state from the form data.
   */
  updateFormState() {
    const formData = new FormData(this.formRef);
    formData.forEach((value, key) => {
      this.setFormState(value, key);
    });

    this.formState = { ...this.formState };
    return this.formState;
  }

  /**
   * Sets the active tab.
   */
  setActiveTab(e: Event, tabName: TabGroup) {
    e.preventDefault();
    this.updateFormState();
    this.activeTab = tabName;
  }

  /**
   * Handles the form submission.
   */
  formSubmissionHandler = async (event: Event) => {
    event.preventDefault();
    const formEvent = await this.formSubmitted.emit();
    debug('[nylas-editor-tabs-group]', 'formSubmissionHandler', formEvent);
    const valid = this.formRef.checkValidity();
    if (!valid || formEvent.defaultPrevented) {
      await this.host.setError({
        message: this.formRef.validationMessage || i18next.t('fixFormErrors'),
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.formErrorTitle'),
      });
      return;
    }
    this.isLoading = true;
    this.hasUnsavedChanges = false;
    const formState = this.updateFormState();
    debug('[nylas-editor-tabs-group]', 'formState', formState);
    const {
      title,
      description,
      bookingCalendar,
      location,
      bookingType,
      minCancellationNotice,
      minBookingNotice,
      cancellationPolicy,
      hideCancellationOptions,
      hideReschedulingOptions,
      additionalFields,
      conferencing,
      reminders,
      emailTemplate,
      redirectUrl,
      slug,
      appearance,
      name,
      eventCapacity,
      eventTimeslot,
      participants,
      disableEmails,
      organizerLocale,
    } = formState;

    const configObject: Partial<GroupConfiguration> = {
      ...(this.selectedConfiguration?.id && { id: this.selectedConfiguration.id }),
      slug: slug,
      name: name,
      type: this.selectedEventType === CONFIGURATION_EVENT_TYPE.group ? CONFIGURATION_EVENT_TYPE.group : undefined,
      scheduler: {
        cancellation_policy: cancellationPolicy ?? '',
        min_cancellation_notice: minCancellationNotice ?? 0,
        min_booking_notice: minBookingNotice ?? 60,
        hide_cancellation_options: hideCancellationOptions ?? false,
        hide_rescheduling_options: hideReschedulingOptions ?? false,
        additional_fields: additionalFields,
        email_template: emailTemplate
          ? {
              ...emailTemplate,
              organizer_locale: organizerLocale || 'en',
              logo: emailTemplate.logo || '',
            }
          : undefined,
        confirmation_redirect_url: redirectUrl,
      },
      appearance: appearance,
      group_booking: {
        booking_type: bookingType ? (bookingType as BookingType) : 'booking',
        disable_emails: typeof disableEmails === 'boolean' ? disableEmails : false,
        reminders: reminders,
        conferencing: conferencing,
        calendar_id: bookingCalendar || 'primary',
        default_capacity: eventCapacity || 10,
      },
    };

    const configObjectWithDeveloperSettings = mergeDeep(this.selectedConfiguration, configObject || {});

    const finalConfig = this.action === 'create' ? configObjectWithDeveloperSettings : configObject;
    // Ensure the following properties are not included in the final config for group events
    delete finalConfig.availability;
    delete finalConfig.participants;
    delete finalConfig.event_booking;
    const resetLoadingState = (_e: CustomEvent) => {
      this.isLoading = false;
      this.changesSaved = true;
      setTimeout(() => {
        this.changesSaved = false;
      }, 3000);
    };

    const hasBookingError = this.checkForBookingFormErrors(finalConfig);

    if (hasBookingError) {
      return;
    }
    const organizer = {
      name: this.currentUser?.name ?? this.currentUser?.email ?? '',
      email: this.currentUser?.email ?? '',
    };
    const organizerDefaultCal = this.calendars?.find(c => c.is_primary) ?? this.calendars?.[0];
    const organizerParticipant = [{ ...organizer, is_organizer: true }];
    const groupEvent = !this.selectedConfiguration?.id
      ? {
          title: title,
          description: description,
          location: !conferencing ? location : undefined,
          conferencing: !location ? conferencing : undefined,
          when: {
            start_time: eventTimeslot?.when?.start_time ?? 0,
            end_time: eventTimeslot?.when?.end_time ?? 0,
            start_timezone: eventTimeslot?.when?.start_timezone ?? '',
            end_timezone: eventTimeslot?.when?.end_timezone ?? '',
          },
          recurrence: eventTimeslot?.recurrence ?? [],
          calendar_id: bookingCalendar || organizerDefaultCal?.id || 'primary',
          participants: participants?.length ? participants : organizerParticipant,
          organizer: organizer,
          capacity: eventCapacity || 10,
        }
      : undefined;
    this.schedulerConfigChanged.emit({ groupEvent: groupEvent || undefined, config: finalConfig, resetLoadingState: resetLoadingState, action: this.action, host: this.host });
  };

  /**
   * Checks for booking form errors.
   */
  checkForBookingFormErrors = config => {
    // Check additional Fields in config
    let hasError = false;
    let errorMessage = '';
    const additionalFields = config.scheduler?.additional_fields;
    if (additionalFields) {
      errorMessage = '';
      Object.entries(additionalFields).forEach(entry => {
        const field = entry[1] as AdditionalFields;
        if (field.label === '') {
          errorMessage += i18next.t('nylasEditorTabs.bookingFormError_label', { type: field.type });
          hasError = true;
          return;
        }
        if (field.options) {
          for (const option of field.options) {
            if (option.trim() === '') {
              errorMessage += i18next.t('nylasEditorTabs.bookingFormError_option', { label: field.label, type: field.type });
              hasError = true;
              break;
            }
          }
        }
      });
    }
    if (hasError) {
      this.isLoading = false;
      this.host.setError({
        message: errorMessage,
        type: NotificationType.Error,
        category: ErrorCategory.Component,
        title: i18next.t('nylasEditorTabs.bookingFormError'),
      });
    }
    return hasError;
  };

  /**
   * Hides the tab.
   */
  hideTab(tab: TabGroup) {
    return this.hideEditorTabs?.includes(tab) || false;
  }

  /**
   * Handles the feedback click event.
   */
  onFeedbackClick(e: Event) {
    e.preventDefault();
    this.showFeedbackModal = true;
  }

  @RegisterComponent<NylasEditorTabsGroup, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-editor-tabs-group',
    stateToProps: new Map([
      ['schedulerConfig.calendars', 'calendars'],
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.currentUser', 'currentUser'],
      ['schedulerConfig.configurations', 'configurations'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.selectedEventType', 'selectedEventType'],
      ['schedulerConfig.selectedGroupEvent', 'selectedGroupEvent'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      schedulerConfigChanged: async (
        event: CustomEvent<SchedulerGroupEventDetail & { host: HTMLNylasEditorTabsGroupElement }>,
        nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { resetLoadingState, host, action, config, groupEvent } = event.detail;
        const checkForErrors = (response: DataResponseReturnType, errorTitle: string) => {
          const [data, error] = response;
          if (!data && error) {
            host.setError({
              message: error.message || i18next.t('genericErrorTitle'),
              type: NotificationType.Error,
              category: ErrorCategory.Component,
              title: errorTitle,
            });
          } else if (data && !error && action === 'create') {
            host.schedulerConfigCreatedEventHandler({ config: data, action, host });
          }
        };

        // If we have an ID, we are updating an existing configuration
        if (action === 'edit') {
          const response = await nylasSchedulerConfigConnector.schedulerConfig.updateConfiguration(config);
          checkForErrors(response, i18next.t('nylasEditorTabs.updateConfigError'));

          if (resetLoadingState) {
            resetLoadingState(event);
          }
        } else {
          const response = await nylasSchedulerConfigConnector.schedulerConfig.createConfiguration(config);
          checkForErrors(response, i18next.t('nylasEditorTabs.createConfigError'));
          const [data, _error] = response;
          const configId = data?.id;

          if (configId && groupEvent) {
            const eventResponse = await nylasSchedulerConfigConnector.schedulerConfig.createGroupEvent(groupEvent, configId);
            checkForErrors(eventResponse, i18next.t('nylasEditorTabsGroup.createGroupEventError'));
            // nylasSchedulerConfigConnector.schedulerConfigStore.set('selectedEventType', undefined);
          }
          if (resetLoadingState) {
            resetLoadingState(event);
          }
        }
      },
      cancelButtonClick: async (_, nylasSchedulerConfigConnector) => {
        nylasSchedulerConfigConnector.schedulerConfigStore.state.action = null;
        nylasSchedulerConfigConnector.schedulerConfigStore.set('selectedEventType', undefined);
        nylasSchedulerConfigConnector.schedulerConfigStore.set('selectedGroupEvent', undefined);
        nylasSchedulerConfigConnector.schedulerConfigStore.set('groupEvents', []);
      },
      previewButtonClicked: async (_, nylasSchedulerConfigConnector) => {
        debug('[nylas-editor-tabs-group]', 'previewButtonClicked', nylasSchedulerConfigConnector);
      },
      formSubmitted: async (_, nylasSchedulerConfigConnector) => {
        debug('[nylas-editor-tabs-group]', 'formSubmitted', nylasSchedulerConfigConnector);
      },
      schedulerEditorFormUpdated: async (event: CustomEvent<{ value: string; name: string }>, _nylasSchedulerConfigConnector) => {
        debug('[nylas-editor-tabs-group]', 'schedulerEditorFormUpdated', event);
      },
      schedulerConfigCreated: async (event: CustomEvent<SchedulerGroupEventDetail>, _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector) => {
        debug('[nylas-editor-tabs-group]', 'schedulerConfigCreated', event);
      },
      groupEventUpdateTriggered: async (
        event: CustomEvent<{
          groupEvent: GroupEvent;
          host: HTMLNylasEditorTabsGroupElement;
          chosenOption: RecurrenceUpdateOption;
          configId: string;
        }>,
        nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { groupEvent, host, chosenOption, configId } = event.detail;

        const response = await nylasSchedulerConfigConnector.schedulerConfig.updateGroupEvent(groupEvent, chosenOption, configId);
        const [data, error] = response;
        if (!data && error) {
          host.setError({
            message: error.message || i18next.t('genericErrorTitle'),
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabsGroup.updateGroupEventError'),
          });
        }
      },
      groupEventDeleteTriggered: async (
        event: CustomEvent<{ groupEvent: GroupEvent; chosenOption: RecurrenceDeleteOption; configId: string; host: HTMLNylasEditorTabsGroupElement }>,
        nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { groupEvent, chosenOption, configId, host } = event.detail;
        const response = await nylasSchedulerConfigConnector.schedulerConfig.deleteGroupEvent(groupEvent, chosenOption, configId);
        const [data, error] = response;
        if (!data && error) {
          host.setError({
            message: error.message || i18next.t('genericErrorTitle'),
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabsGroup.deleteGroupEventError'),
          });
        }
      },
      groupEventCreateTriggered: async (
        event: CustomEvent<{ groupEvent: Partial<GroupEvent>; configId: string; host: HTMLNylasEditorTabsGroupElement }>,
        nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { groupEvent, configId, host } = event.detail;
        const response = await nylasSchedulerConfigConnector.schedulerConfig.createGroupEvent(groupEvent, configId);
        const [data, error] = response;
        if (!data && error) {
          host.setError({
            message: error.message || i18next.t('genericErrorTitle'),
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabsGroup.createGroupEventError'),
          });
        }
      },
      groupEventImportTriggered: async (
        event: CustomEvent<{
          groupEvent: ImportGroupEventDetails;
          configId: string;
          host: HTMLNylasEditorTabsGroupElement;
        }>,
        nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { groupEvent, configId, host } = event.detail;
        const response = await nylasSchedulerConfigConnector.schedulerConfig.importGroupEvent([groupEvent], configId);
        const [data, error] = response;
        if (!data && error) {
          host.setError({
            message: error.message || i18next.t('genericErrorTitle'),
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabsGroup.importGroupEventError'),
          });
        }
        if (data && data.import_failed && data.import_failed.length > 0) {
          const errorMessage = data.import_failed.map(failedEvent => failedEvent.reason).join('\n');
          host.setError({
            message: errorMessage,
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabsGroup.importGroupEventError'),
          });
          setTimeout(() => {
            host.resetError();
          }, 5000);
        }
      },
      saveGroupEventChangesError: async (
        event: CustomEvent<{
          error: string;
          configId: string;
          host: HTMLNylasEditorTabsGroupElement;
        }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { error, host } = event.detail;
        debug('[nylas-editor-tabs-group]', 'saveGroupEventChangesError', error, host);
      },
    },
    localPropsToProp: new Map([
      ['schedulerPreviewLink', 'schedulerPreviewLink'],
      ['enableUserFeedback', 'enableUserFeedback'],
      ['hideEditorTabs', 'hideEditorTabs'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    debug('[nylas-editor-tabs-group]', 'render', this.formState);
    // TODO: Remove this check when the feature is ready to be released
    // const showInDevelopmentTabs = localStorage.getItem('nylas') === 'Nylas Developer';

    return (
      <Host>
        <nylas-notification></nylas-notification>
        <form onSubmit={this.formSubmissionHandler} class="scheduler-editor" ref={el => (this.formRef = el as HTMLFormElement)} noValidate>
          {this.mode === 'app' ? (
            <div class="return-to-calendar-view" part="editor__return-to-calendar-view net__return-to-calendar-view">
              <button-component
                id="return-to-calendar-view-button"
                variant="invisible"
                part="nset__header__back-button"
                type="button"
                onClick={e => {
                  this.setActiveTab(e, TabGroup.CalendarView);
                }}
              >
                <chevron-icon width="16" height="16" />
                <span>{i18next.t('returnToCalendarView')}</span>
              </button-component>
            </div>
          ) : null}
          {this.mode === 'app' ? (
            <div class="form-contents" part="editor__form-contents net__form-contents">
              <div class="tabs" part="editor__tabs net__tabs">
                <button
                  name={TabGroup.BookingOptions}
                  class={{ tab: true, active: this.activeTab == TabGroup.BookingOptions, hide: this.hideTab(TabGroup.BookingOptions) }}
                  part="editortab__booking-options net__tab-booking-options"
                  onClick={e => this.setActiveTab(e, TabGroup.BookingOptions)}
                >
                  <flow-icon width="16" height="16" />
                  {i18next.t('bookingOptionsTab')}
                </button>
                <button
                  name={TabGroup.BookingForm}
                  class={{ tab: true, active: this.activeTab == TabGroup.BookingForm, hide: this.hideTab(TabGroup.BookingForm) }}
                  part="editortab__booking-form net__tab-booking-form"
                  onClick={e => this.setActiveTab(e, TabGroup.BookingForm)}
                >
                  {this.activeTab == TabGroup.BookingForm ? <calendar-agenda-fill-icon width="16" height="16" /> : <calendar-agenda-icon width="16" height="16" />}
                  {i18next.t('bookingFormTab')}
                </button>
                <button
                  name={TabGroup.Communications}
                  class={{ tab: true, active: this.activeTab == TabGroup.Communications, hide: this.hideTab(TabGroup.Communications) }}
                  onClick={e => this.setActiveTab(e, TabGroup.Communications)}
                  part="editortab__communications net__tab-communications"
                >
                  {this.activeTab == TabGroup.Communications ? <envelope-fill-icon width="16" height="16" /> : <envelope-icon width="16" height="16" />}
                  {i18next.t('communicationsTab')}
                </button>
                <button
                  name={TabGroup.PageStyles}
                  class={{ tab: true, active: this.activeTab == TabGroup.PageStyles, hide: this.hideTab(TabGroup.PageStyles) }}
                  onClick={e => this.setActiveTab(e, TabGroup.PageStyles)}
                  part="editortab__page-styles net__tab-page-styles"
                >
                  {this.activeTab == TabGroup.PageStyles ? <paintbrush-fill-icon width="16" height="16" /> : <paintbrush-icon width="16" height="16" />}
                  {i18next.t('pageStylesTab')}
                </button>
              </div>
              <div class="tab-content" part="editor__tab-content net__tab-content">
                <TabContentsGroup
                  formState={this.formState}
                  activeTab={this.activeTab}
                  currentUser={this.currentUser}
                  calendars={this.calendars}
                  id={this.selectedConfiguration?.id ?? 'new'}
                  i18next={i18next}
                />
              </div>
            </div>
          ) : (
            <div class="composable" key={this.selectedConfiguration?.id}>
              <slot></slot>
            </div>
          )}

          <div class="footer" part="editor__footer net__footer">
            <div class="preview" part="editor__footer-preview net__footer-preview">
              {this.enableUserFeedback && (
                <button-component title="Share your feedback" id="report-issue" variant={'basic'} onClick={e => this.onFeedbackClick(e)}>
                  <feedback-icon />
                </button-component>
              )}
              {this.activeTab === TabGroup.CalendarView && (
                <button-component
                  class="page-settings-button"
                  title="Go to page settings"
                  id="page-settings"
                  variant={'basic'}
                  onClick={e => this.setActiveTab(e, TabGroup.BookingOptions)}
                >
                  {i18next.t('pageSettingsButton')}
                </button-component>
              )}
            </div>
            <div class="buttons" part="editor__footer-buttons net__footer-buttons">
              <p
                class={{
                  'unsaved-changes': this.hasUnsavedChanges && !this.changesSaved,
                  'saved-changes': this.changesSaved,
                  'error': this.error?.message !== '',
                  'error-full-width': this.error?.message !== '' && this.action === 'create',
                }}
              >
                {this.error?.message == '' && this.hasUnsavedChanges && !this.changesSaved && this.activeTab !== TabGroup.CalendarView
                  ? i18next.t('unsavedChangesLabel')
                  : this.error?.message == '' && this.changesSaved
                    ? `${i18next.t('changesSavedLabel')}!`
                    : ''}
              </p>
              <div class="footer-buttons__row">
                {this.schedulerPreviewLink !== '' && this.action === 'edit' && (
                  <button-component
                    title="Preview"
                    type="button"
                    disabled={this.isLoading}
                    clickHandler={event => {
                      event.preventDefault();
                      const previewEvent = this.previewButtonClicked.emit(this.host);
                      if (!previewEvent.defaultPrevented) {
                        window.open(parsePreviewLink(this.schedulerPreviewLink, this.selectedConfiguration), '_blank');
                      }
                    }}
                    variant={'basic'}
                  >
                    <eye-icon />
                  </button-component>
                )}
                <button-component
                  type="button"
                  disabled={this.isLoading}
                  clickHandler={event => {
                    event.preventDefault();
                    this.cancelButtonClick.emit();
                    this.host.resetError();
                  }}
                  variant={'basic'}
                  part="editor__footer-cancel net__footer-cancel"
                >
                  {i18next.t('cancelButton')}
                </button-component>
                <button-component class={{ 'save-changes': this.action === 'edit' }} type="submit" disabled={this.isLoading} part="editor__footer-cta, net__footer-cta">
                  {!this.componentLoaded ? (
                    <span>
                      <loading-icon />{' '}
                    </span>
                  ) : this.isLoading ? (
                    <span>
                      {i18next.t('savingButtonText')}
                      <loading-icon />{' '}
                    </span>
                  ) : this.action === 'create' ? (
                    `${i18next.t('createButtonText')}`
                  ) : (
                    `${i18next.t('saveChangesButtonText')}`
                  )}
                </button-component>
              </div>
            </div>
          </div>
        </form>
        {this.showFeedbackModal && <nylas-feedback-form></nylas-feedback-form>}
      </Host>
    );
  }
}
